import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useInitialParamsSnapshot } from '../hooks/useInitialParams';
import { usePlayerBootstrap } from '../hooks/usePlayerBootstrap';
import { useTenant } from '../FeatureFlags';
import { useRetailerOptions } from '../hooks/useRetailerOptions';
import { RetailerOptionList } from '../components/RetailerOptionList';
import { postMessageChangeStep } from '../utils/postMessage';

export function SelectRegionPage() {
  const navigate = useNavigate();
  const { region, setRegion, setRetailer, storeFilter } =
    useInitialParamsSnapshot();
  const { copy, flow } = useTenant();

  const options = useRetailerOptions();

  const defaultSelected = useMemo<string>(() => {
    const match = options.find((o) => o.region === region);
    return match?.retailer_key ?? options[0]?.retailer_key ?? 'amazon_us';
  }, [options, region]);

  const [selected, setSelected] = useState<string>(defaultSelected);
  usePlayerBootstrap();

  // Signal the embedding parent (survey host) that the user reached /connect.
  useEffect(() => {
    postMessageChangeStep(1);
  }, []);

  const handleContinue = (
    option = options.find((o) => o.retailer_key === selected)
  ) => {
    if (!option) return;

    if (option.region) setRegion(option.region as 'US' | 'CA');
    setRetailer(option.retailer_id);
    navigate('/connect/signin');
  };

  // If the URL locked us to a single retailer (e.g. ?s=amazon_us), skip this
  // picker entirely and go straight to sign-in.
  useEffect(() => {
    if (storeFilter.length === 1 && options.length === 1) {
      handleContinue(options[0]);
    }
    // oxlint-disable-next-line react/exhaustive-deps
  }, [options]);

  if (storeFilter.length === 1 && options.length === 1) return null;

  return (
    <div className="flex-1 flex flex-col items-center justify-center py-12 px-6">
      <div className="w-full max-w-[480px]">
        <h2 className="font-display text-[clamp(22px,3vw,28px)] font-bold text-ink tracking-[-0.02em] leading-[1.2] mb-2">
          {copy.regionTitle}
        </h2>
        <p className="text-[15px] text-ink-muted mb-8">{copy.regionSubtitle}</p>

        {/* Store cards */}
        <div className="mb-8">
          <RetailerOptionList
            options={options}
            selectedKey={selected}
            onSelect={setSelected}
          />
        </div>

        {/* Continue */}
        <button
          data-testid="retailer-select-continue"
          onClick={() => handleContinue()}
          className="w-full flex items-center justify-center gap-2 py-4 bg-primary hover:bg-primary-deep text-white font-body font-semibold text-[15px] rounded-full transition-colors duration-150 active:scale-[0.985]"
        >
          {flow.chromeless ? 'Connect' : 'Continue'}
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M9 5l7 7-7 7"
            />
          </svg>
        </button>
      </div>
    </div>
  );
}
